/**
 * File Service for handling friendly URLs
 * This service helps generate and manage friendly URLs for files stored in Firebase Storage
 */

class FileService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_FIREBASE_HOSTING_URL || window.location.origin;
  }

  /**
   * Generate a friendly URL for a file
   * @param {string} fileName - The name of the file
   * @param {string} folder - Optional folder path
   * @returns {string} - Friendly URL
   */
  getFriendlyUrl(fileName, folder = '') {
    if (!fileName) return '';
    
    const path = folder ? `${folder}/${fileName}` : fileName;
    return `${this.baseUrl}/docs/${path}`;
  }

  /**
   * Generate a friendly URL for a PDF file
   * @param {string} fileName - The name of the file (without .pdf extension)
   * @param {string} folder - Optional folder path
   * @returns {string} - Friendly URL
   */
  getPdfUrl(fileName, folder = '') {
    const fullFileName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
    return this.getFriendlyUrl(fullFileName, folder);
  }

  /**
   * Generate a friendly URL for a journal article
   * @param {string} articleId - The article ID or filename
   * @param {string} issue - The issue number
   * @param {string} volume - The volume number
   * @returns {string} - Friendly URL
   */
  getJournalUrl(articleId, issue = '', volume = '') {
    const folder = volume && issue ? `journals/volume-${volume}/issue-${issue}` : 'journals';
    return this.getPdfUrl(articleId, folder);
  }

  /**
   * Generate a friendly URL for an editorial note
   * @param {string} fileName - The filename
   * @param {string} issue - The issue number
   * @param {string} volume - The volume number
   * @returns {string} - Friendly URL
   */
  getEditorialUrl(fileName, issue = '', volume = '') {
    const folder = volume && issue ? `editorials/volume-${volume}/issue-${issue}` : 'editorials';
    return this.getPdfUrl(fileName, folder);
  }

  /**
   * Generate a friendly URL for guidelines or other documents
   * @param {string} fileName - The filename
   * @param {string} category - The document category (e.g., 'guidelines', 'forms')
   * @returns {string} - Friendly URL
   */
  getDocumentUrl(fileName, category = 'documents') {
    return this.getPdfUrl(fileName, category);
  }

  /**
   * Extract file information from a Firebase Storage URL
   * @param {string} firebaseUrl - The Firebase Storage URL
   * @returns {object} - Object containing fileName and folder
   */
  parseFirebaseUrl(firebaseUrl) {
    if (!firebaseUrl) return { fileName: '', folder: '' };

    try {
      const url = new URL(firebaseUrl);
      const pathParts = url.pathname.split('/');
      
      // Remove empty parts and get the file path
      const filePath = pathParts.filter(part => part).join('/');
      
      if (!filePath) return { fileName: '', folder: '' };

      const lastSlashIndex = filePath.lastIndexOf('/');
      
      if (lastSlashIndex === -1) {
        return { fileName: filePath, folder: '' };
      }

      const fileName = filePath.substring(lastSlashIndex + 1);
      const folder = filePath.substring(0, lastSlashIndex);

      return { fileName, folder };
    } catch (error) {
      console.error('Error parsing Firebase URL:', error);
      return { fileName: '', folder: '' };
    }
  }

  /**
   * Convert a Firebase Storage URL to a friendly URL
   * @param {string} firebaseUrl - The Firebase Storage URL
   * @returns {string} - Friendly URL
   */
  convertToFriendlyUrl(firebaseUrl) {
    const { fileName, folder } = this.parseFirebaseUrl(firebaseUrl);
    return this.getFriendlyUrl(fileName, folder);
  }

  /**
   * Get the list of files in a directory
   * @param {string} folder - The folder path
   * @returns {Promise<Array>} - Array of file objects
   */
  async listFiles(folder = '') {
    try {
      const response = await fetch(`${this.baseUrl}/docs/list/${folder}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.files || [];
    } catch (error) {
      console.error('Error listing files:', error);
      return [];
    }
  }

  /**
   * Check if a file exists
   * @param {string} fileName - The name of the file
   * @param {string} folder - Optional folder path
   * @returns {Promise<boolean>} - Whether the file exists
   */
  async fileExists(fileName, folder = '') {
    try {
      const url = this.getFriendlyUrl(fileName, folder);
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      console.error('Error checking file existence:', error);
      return false;
    }
  }
}

// Create and export a singleton instance
const fileService = new FileService();
export default fileService;
